import axios, { AxiosRequestConfig } from 'axios';
import { getSession } from 'next-auth/react';

// Extend NextAuth Session type to include accessToken
declare module 'next-auth' {
  interface Session {
    accessToken?: string;
  }
}

// Get the API base URL from environment variables
const getBaseURL = () => {
  const isTestEnv =
    process.env.NODE_ENV === 'test' || process.env.VITEST === 'true';

  if (typeof window !== 'undefined') {
    if (isTestEnv) {
      // Keep relative versioned paths in tests so existing MSW handlers match.
      return '';
    }

    // Client-side: always route through the Next.js proxy so auth headers are
    // added server-side and the real backend URL is never exposed to the browser.
    return '/api/event-management';
  } else {
    // Server-side: call the backend directly.
    return process.env.EVENT_MANAGEMENT_SERVICE_URL || 'https://localhost:7049';
  }
};

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth headers or other modifications
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get session and add auth token
    if (typeof window !== 'undefined') {
      const session = await getSession();
      if (session?.accessToken) {
        config.headers.Authorization = `Bearer ${session.accessToken}`;
      } else {
        console.warn('[Event Management API] No access token found in session');
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(
        `[Event Management API] ${config.method?.toUpperCase()} ${config.url}`
      );
      console.log('[Event Management API] Headers:', config.headers);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        '[Event Management API Error]:',
        error.response?.status,
        error.response?.data
      );
    }

    // Enhance error object with response data for better error handling
    if (error.response?.data) {
      // Merge the error response data (which may contain type, title, detail) into the error object
      const enhancedError = Object.assign(error, {
        type: error.response.data.type,
        title: error.response.data.title,
        detail: error.response.data.detail,
        status: error.response.data.status || error.response.status,
        traceId: error.response.data.traceId,
      });
      return Promise.reject(enhancedError);
    }

    return Promise.reject(error);
  }
);

// Path rewriting to match backend routes
const rewritePath = (url: string): string => {
  // Remove the /api/event-management prefix and keep only the versioned path
  return url.replace(/^\/api\/event-management\/v(\d+)/, '/v$1');
};

const mutator = <T>(
  config: AxiosRequestConfig,
  configOverride?: AxiosRequestConfig
): Promise<T> => {
  const rewrittenConfig = {
    ...config,
    url: config.url ? rewritePath(config.url) : config.url,
    ...configOverride,
  };

  return axiosInstance<T>(rewrittenConfig).then((response) => response.data);
};

export default mutator;
